import BookkeepingManagerStore from 'admin/BookkeepingManagerStore'
import InventoryStore from 'admin/InventoryStore'
import BookkeepingAssessment from 'admin/bookkeeping/BookkeepingAssessment'
import BookkeepingPendingAction, {
  BookkeepingManagerAction,
} from 'admin/bookkeeping/BookkeepingPendingAction'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { GetCurrentMember } from 'iam/SessionStore'
import link from 'infra/link'
import Job from 'jobs/Job'
import JobStore, {
  ScopeChangeInventoryParams,
  ScopeChangeParams,
} from 'jobs/JobStore'
import RequestScopeChangesViewModel from 'jobs/views/JobRoot/Job/RequestScopeChangesDialog/RequestScopeChangesViewModel'
import UpdateJobScopeViewModel from 'jobs/views/JobRoot/Job/UpdateJobScopeDialog/UpdateJobScopeViewModel'
import { noop } from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Subject } from 'rxjs'
import { Nullable } from 'types/definitions'

export default class BookkeepingManagerDetailsViewModel {
  /**
   * Whether we should show the bookkeeping pending action details.
   */
  @observable
  showing = false

  /**
   * Subject used to emit when details is updated.
   */
  private readonly update = new Subject<BookkeepingPendingAction>()

  /**
   * Emits the bookkeeping pending action when details is updated.
   */
  readonly update$ = this.update.asObservable()

  /*
   * The bookkeeping's job.
   */
  @observable
  job: Nullable<Job> = null

  /**
   * The bookkeeping pending action.
   */
  @observable
  bookkeepingPendingAction = BookkeepingPendingAction.none

  /**
   * The bookkeeping assessment.
   */
  @observable
  bookkeepingAssessment = BookkeepingAssessment.none

  /**
   * Admin job scope update view model.
   */
  @observable
  updateJobScopeVM: Nullable<UpdateJobScopeViewModel> = null

  /**
   * Request scope changes view model.
   */
  @observable
  requestScopeChangesVM: Nullable<RequestScopeChangesViewModel> = null

  constructor(
    private bookkeepingManagerStore: BookkeepingManagerStore,
    private newAlert: AlertFactory,
    private closeNav: () => void,
    private jobStore: JobStore,
    private inventoryStore: InventoryStore,
    private getCurrentMember: GetCurrentMember,
  ) {
    makeObservable(this)
  }

  /**
   * Whether the scope changes are settled.
   */
  @computed
  get scopeChangesSettled() {
    if (!this.job) {
      return false
    }

    return this.jobStore.scopeChangesSettled(this.job)
  }

  /**
   * Whether the user can update the job scope.
   */
  @computed
  get canUpdateJobScope() {
    if (!this.job?.canUpdateJobScope) {
      return false
    }

    return this.scopeChangesSettled
  }

  /**
   * Whether we can request scope changes.
   */
  @computed
  get canRequestScopeChanges() {
    if (!this.job?.canRequestScopeChanges) {
      return false
    }

    return this.scopeChangesSettled
  }

  /**
   * Latest scope change
   */
  @computed
  get latestScopeChange() {
    if (!this.job?.canRequestScopeChanges && !this.job?.canUpdateJobScope) {
      return null
    }

    const scopeChanges = this.jobStore.scopeChangesForJob(this.job)
    if (!scopeChanges.length) {
      return null
    }

    return scopeChanges
      .sort((x, y) => x.createTime.getTime() - y.createTime.getTime())
      .at(-1)!
  }

  /**
   * Sets the job.
   *
   * @param job
   */
  @action.bound
  setJob(job: Job | null) {
    this.job = job
  }

  /**
   * Sets the bookkeeping pending action.
   *
   * @param bookkeepingPendingAction
   */
  @action.bound
  setBookkeepingPendingAction(
    bookkeepingPendingAction: BookkeepingPendingAction,
  ) {
    this.bookkeepingPendingAction = bookkeepingPendingAction
  }

  /**
   * Sets the bookkeeping assessment.
   *
   * @param bookkeepingAssessment
   */
  @action.bound
  setBookkeepingAssessment(bookkeepingAssessment: BookkeepingAssessment) {
    this.bookkeepingAssessment = bookkeepingAssessment
  }

  /**
   * Sets the update job scope view model.
   *
   * @param job
   */
  @action.bound
  setUpdateJobScopeVM(job: Job) {
    this.updateJobScopeVM = new UpdateJobScopeViewModel(
      job,
      this.inventoryStore,
      job.workspace.id,
      this.jobStore,
    )

    this.updateJobScopeVM.close$.subscribe(async () => {
      await this.fetchScopeChanges()
    })
  }

  /**
   * Sets the request scope change view model.
   *
   * @param job
   */
  @action.bound
  setRequestScopeChangesVM(job: Job) {
    this.requestScopeChangesVM = new RequestScopeChangesViewModel(
      job,
      this.inventoryStore,
      job.workspace.id,
      async (
        reason: string,
        changes: ScopeChangeParams[],
        inventoryChanges: ScopeChangeInventoryParams[],
      ) => {
        const response = await this.jobStore.requestScopeChanges(
          job,
          reason,
          changes,
          inventoryChanges,
        )

        await this.fetchScopeChanges()

        return response
      },
      noop,
      this.getCurrentMember,
    )
  }

  /**
   * Shows the bookkeeping pending action details.
   *
   * @param bookkeepingPendingAction
   */
  @action.bound
  show(bookkeepingPendingAction: BookkeepingPendingAction) {
    const action = bookkeepingPendingAction.action

    this.showing = true
    this.closeNav()
    this.setBookkeepingPendingAction(bookkeepingPendingAction)

    if (action === BookkeepingManagerAction.AssessmentReview) {
      this.fetchAssessment()
      this.fetchJob()
      this.fetchScopeChanges()
    } else {
      this.setBookkeepingAssessment(BookkeepingAssessment.none)
      this.setJob(null)

      this.updateJobScopeVM?.close()
      this.requestScopeChangesVM?.close()

      this.updateJobScopeVM = null
      this.requestScopeChangesVM = null
    }
  }

  /**
   * Fetches bookkeeping assessment.
   */
  async fetchAssessment() {
    const assessment = await this.bookkeepingManagerStore.fetchAssessment(
      this.bookkeepingPendingAction.id,
    )

    runInAction(() => this.setBookkeepingAssessment(assessment))
  }

  /**
   * Fetches job.
   *
   * @returns
   */
  async fetchJob() {
    const job = await this.jobStore.fetchJob(this.bookkeepingPendingAction.id)

    runInAction(() => {
      this.setJob(job)
      this.setUpdateJobScopeVM(job)
      this.setRequestScopeChangesVM(job)
    })
  }

  /**
   * Fetch scope changes.
   *
   * @returns
   */
  async fetchScopeChanges() {
    await this.jobStore.listScopeChangesForCollaborator(
      this.bookkeepingPendingAction.id,
    )
  }

  /**
   * Closes the bookkeeping pending action details.
   */
  @action.bound
  close() {
    this.showing = false

    this.setBookkeepingPendingAction(BookkeepingPendingAction.none)
    this.setBookkeepingAssessment(BookkeepingAssessment.none)
    this.setJob(null)

    this.updateJobScopeVM?.close()
    this.requestScopeChangesVM?.close()

    this.updateJobScopeVM = null
    this.requestScopeChangesVM = null
  }

  /**
   * Confirms the bookkeeping pending action platform access.
   */
  @action.bound
  confirmAccess = task.resolved(async () => {
    return this.bookkeepingManagerStore
      .confirmManagedPlatformAccess(this.bookkeepingPendingAction.id)
      .then(() => this.update.next(this.bookkeepingPendingAction))
      .then(this.notifyAccessConfirmed)
      .then(this.close)
      .catch(this.handleError)
  })

  /**
   * Completes the review.
   */
  @action.bound
  completeReview = task.resolved(async () => {
    return this.bookkeepingManagerStore
      .completeBookkeepingAssessmentReview(this.bookkeepingPendingAction.id)
      .then(() => this.update.next(this.bookkeepingPendingAction))
      .then(this.notifyReivewCompleted)
      .then(this.close)
      .catch(this.handleError)
  })

  /**
   * Views admin change requests.
   */
  @action.bound
  viewChangeRequests() {
    window.open(link.adminChangeRequests(), '_blank')
  }

  /**
   * Displays a message notifying the access was confirmed.
   */
  @action.bound
  private notifyAccessConfirmed() {
    this.newAlert()
      .withState(AlertState.Success)
      .saying('You confirmed bookkeeping access.')
      .show()
      .autoDismiss()
  }

  /**
   * Displays a message notifying the assessment review was completed.
   */
  @action.bound
  private notifyReivewCompleted() {
    this.newAlert()
      .withState(AlertState.Success)
      .saying('You complete the assessment review.')
      .show()
      .autoDismiss()
  }

  /**
   * Handles error displaying the error message.
   */
  @action.bound
  private handleError(error: Error) {
    this.newAlert().fromError(error).show()
    console.error(error)
  }
}
